import React, { FC, useMemo } from "react";
import { Flex, Button, ModalBody, ModalFooter, Text } from "@chakra-ui/react";
import { useToast } from "@/components/ui/Toast";
import { ClassroomOverviewDTO } from "@/stores/classroomStore_I";
import useStore from "@/stores/index";
import { getClassroomStore } from "@/stores/classroomStore";
import { ClassCombinationResponse } from "@/js/api/combineClassesApi";
import { ClasswizeModal } from "@/components/ui/Modal";

type props = {
	open: boolean;
	onClose: () => void;
	classroom: ClassroomOverviewDTO;
	classCombination: ClassCombinationResponse[];
	isSaving: boolean;
	setIsSaving: (isSaving: boolean) => void;
};

const headerText = "Split Combined Classes";
const bodyText1 = "This action will split the classes that are combined within";
const bodyText2 = "into individual classes.";
const descText = "After splitting, you can run these classes as usual.";
const buttonText = "Split Classes";
const successNotificationTitle = "The classes have split successfully";
const successNotificationText = "You can now run the classes individually.";
const errorNotificationTitle = "There was an error splitting the classes that are combined within";
const errorNotificationText = "Please go to class settings and try again";

export const SplitModal: FC<props> = ({ open, onClose, classroom, isSaving, setIsSaving }) => {
	const { successToast, errorToast } = useToast();

	const title = useMemo(() => classroom.personalisation?.displayName ?? "", [classroom]);

	const handleSplit = async () => {
		const { handleDeleteClassroom, fetchClassrooms } = getClassroomStore(useStore.getState());

		try {
			setIsSaving(true);

			await handleDeleteClassroom(classroom.stableIdV1);
			await fetchClassrooms();
			successToast({
				title: successNotificationTitle,
				description: successNotificationText,
				isClosable: true,
			});
		} catch (e: unknown) {
			console.error(e);
			errorToast({
				title: `${errorNotificationTitle} ${title}`,
				description: errorNotificationText,
				isClosable: true,
			});
		} finally {
			setIsSaving(false);
			onClose();
		}
	};

	return (
		<ClasswizeModal title={headerText} size="lg" isOpen={open} onClose={onClose}>
			<ModalBody>
				<Text color="#051839" fontSize="16px" lineHeight="20px" data-testid="body-text">
					{bodyText1} <b>{title}</b> {bodyText2}
				</Text>
				<br />
				<Text color="#051839" fontSize="16px" lineHeight="20px">
					{descText}
				</Text>
			</ModalBody>

			<ModalFooter>
				<Flex direction="row-reverse">
					<Button onClick={handleSplit} borderRadius="md" data-testid="modal-split-btn" isLoading={isSaving}>
						{buttonText}
					</Button>
				</Flex>
			</ModalFooter>
		</ClasswizeModal>
	);
};
